import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthContext } from '../context/AuthContext';
import { Application } from '@splinetool/runtime';

const LoginPage = () => {
  const navigate = useNavigate();
  const { login, verifyOTP, completeRegistration, waitingForOTP, isNewUser } = useAuthContext();
  
  // Form states
  const [email, setEmail] = useState('');
  const [otp, setOtp] = useState(['', '', '', '', '', '']);
  const [fullname, setFullname] = useState('');
  const [experience, setExperience] = useState('');
  
  // UI states
  const [currentStep, setCurrentStep] = useState('email');
  const [isLoading, setIsLoading] = useState(false);
  const [resendTimer, setResendTimer] = useState(0);
  const [displayEmail, setDisplayEmail] = useState('');
  
  // Refs
  const otpInputs = useRef([]);
  const resendIntervalRef = useRef(null);
  const splineAppRef = useRef(null);
  const canvasRef = useRef(null);
  const resizeTimeoutRef = useRef(null);
  const animationFrameRef = useRef(null);

  // Debounced resize handler
  const debouncedResize = useCallback(() => {
    if (resizeTimeoutRef.current) {
      clearTimeout(resizeTimeoutRef.current);
    }
    
    resizeTimeoutRef.current = setTimeout(() => {
      if (canvasRef.current && splineAppRef.current) {
        // Use RAF for smooth resizing
        if (animationFrameRef.current) {
          cancelAnimationFrame(animationFrameRef.current);
        }
        
        animationFrameRef.current = requestAnimationFrame(() => {
          const canvas = canvasRef.current;
          if (canvas) {
            const rect = canvas.parentElement.getBoundingClientRect();
            canvas.width = rect.width;
            canvas.height = rect.height;
            
            // Only call resize if method exists
            if (splineAppRef.current && typeof splineAppRef.current.resize === 'function') {
              try {
                splineAppRef.current.resize();
              } catch (e) {
    
              }
            }
          }
        });
      }
    }, 150); // 150ms debounce
  }, []);

  // Initialize Spline with better performance
  const initializeSpline = useCallback(async () => {
    try {
      // Check if already initialized
      if (splineAppRef.current) {
        return;
      }

      const splineContainer = document.getElementById('spline-container');
      if (!splineContainer) return;

      // Create loading indicator
      const loadingIndicator = document.createElement('div');
      loadingIndicator.className = 'absolute inset-0 flex items-center justify-center bg-black/80 backdrop-blur-sm';
      loadingIndicator.innerHTML = `
        <div class="flex flex-col items-center">
          <div class="w-8 h-8 rounded-full border-2 border-zinc-700 border-t-green-500 animate-spin mb-3"></div>
          <p class="text-zinc-400 text-xs">Loading experience...</p>
        </div>
      `;
      splineContainer.appendChild(loadingIndicator);

      // Create canvas with optimal settings
      const canvas = document.createElement('canvas');
      canvas.id = 'spline-canvas';
      canvasRef.current = canvas;
      
      // Initial sizing
      const rect = splineContainer.getBoundingClientRect();
      canvas.width = rect.width;
      canvas.height = rect.height;
      canvas.style.cssText = `
        width: 100%;
        height: 100%;
        display: block;
        position: absolute;
        top: 0;
        left: 0;
      `;
      
      splineContainer.appendChild(canvas);

      // Initialize Spline app with performance settings
      const app = new Application(canvas);
      splineAppRef.current = app;

      // Add performance monitoring
      const startTime = performance.now();

      // Load scene with timeout
      const loadPromise = app.load('https://prod.spline.design/kZDDjO5HuC9GJUM2/scene.splinecode');
      const timeoutPromise = new Promise((_, reject) => 
        setTimeout(() => reject(new Error('Spline loading timeout')), 15000)
      );

      await Promise.race([loadPromise, timeoutPromise]);


      // Remove loading indicator with smooth transition
      if (loadingIndicator && loadingIndicator.parentNode) {
        loadingIndicator.style.transition = 'opacity 0.3s ease';
        loadingIndicator.style.opacity = '0';
        setTimeout(() => {
          if (loadingIndicator.parentNode) {
            loadingIndicator.remove();
          }
        }, 300);
      }

      // Add resize listener with debouncing
      window.addEventListener('resize', debouncedResize, { passive: true });

    } catch (error) {
    
      handleSplineError();
    }
  }, [debouncedResize]);

  // Better fallback handler
  const handleSplineError = useCallback(() => {
    const splineContainer = document.getElementById('spline-container');
    if (!splineContainer) return;
    
    // Animated fallback background
    splineContainer.innerHTML = `
      <div class="absolute inset-0 bg-gradient-to-br from-zinc-900 via-black to-zinc-800 animate-gradient-shift"></div>
      <div class="absolute inset-0 opacity-20">
        <div class="absolute top-1/4 left-1/4 w-32 h-32 bg-green-500/10 rounded-full blur-xl animate-pulse"></div>
        <div class="absolute bottom-1/4 right-1/4 w-24 h-24 bg-blue-500/10 rounded-full blur-xl animate-pulse" style="animation-delay: 1s;"></div>
      </div>
    `;
    
    // Add fallback styles
    if (!document.getElementById('fallback-styles')) {
      const style = document.createElement('style');
      style.id = 'fallback-styles';
      style.textContent = `
        @keyframes gradient-shift {
          0%, 100% { background-position: 0% 50%; }
          50% { background-position: 100% 50%; }
        }
        .animate-gradient-shift {
          background-size: 200% 200%;
          animation: gradient-shift 8s ease infinite;
        }
      `;
      document.head.appendChild(style);
    }
  }, []);

  // Cleanup function
  const cleanupSpline = useCallback(() => {
    // Cancel any pending animations
    if (animationFrameRef.current) {
      cancelAnimationFrame(animationFrameRef.current);
    }
    
    // Clear timeouts
    if (resizeTimeoutRef.current) {
      clearTimeout(resizeTimeoutRef.current);
    }
    
    // Remove event listeners
    window.removeEventListener('resize', debouncedResize);
    
    // Cleanup Spline app
    if (splineAppRef.current) {
      try {
        if (typeof splineAppRef.current.dispose === 'function') {
          splineAppRef.current.dispose();
        }
      } catch (e) {

      }
      splineAppRef.current = null;
    }
    
    // Clear canvas ref
    canvasRef.current = null;
  }, [debouncedResize]);

  // Initialize on mount
  useEffect(() => {
    // Delay initialization slightly to avoid blocking main thread
    const initTimer = setTimeout(() => {
      initializeSpline();
    }, 100);
    
    return () => {
      clearTimeout(initTimer);
      cleanupSpline();
      
      if (resendIntervalRef.current) {
        clearInterval(resendIntervalRef.current);
      }
    };
  }, [initializeSpline, cleanupSpline]);

  // DÜZELTME: Tüm kullanıcılar için OTP adımı
  useEffect(() => {
    if (waitingForOTP) {
      // Hem yeni hem mevcut kullanıcılar OTP adımına gider
      setCurrentStep('otp');
    }
  }, [waitingForOTP]);

  // Optimized toast function
  const showToast = useCallback((message, type = 'info') => {
    const toastContainer = document.getElementById('toast-container');
    if (!toastContainer) return;
    
    const toast = document.createElement('div');
    toast.className = 'flex items-center rounded-lg shadow-xl backdrop-blur-sm transition-all duration-300 mb-3 max-w-sm overflow-hidden';
    
    let iconColor, icon, bgGradient;
    
    switch (type) {
      case 'success':
        toast.classList.add('border-l-4', 'border-green-500', 'text-white');
        bgGradient = 'linear-gradient(to right, rgba(20, 83, 45, 0.9), rgba(24, 24, 27, 0.95))';
        iconColor = 'text-green-400';
        icon = `<svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M20 6L9 17l-5-5"></path></svg>`;
        break;
      case 'error':
        toast.classList.add('border-l-4', 'border-red-500', 'text-white');
        bgGradient = 'linear-gradient(to right, rgba(127, 29, 29, 0.9), rgba(24, 24, 27, 0.95))';
        iconColor = 'text-red-400';
        icon = `<svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"></circle><line x1="15" y1="9" x2="9" y2="15"></line><line x1="9" y1="9" x2="15" y2="15"></line></svg>`;
        break;
      case 'warning':
        toast.classList.add('border-l-4', 'border-yellow-500', 'text-white');
        bgGradient = 'linear-gradient(to right, rgba(113, 63, 18, 0.9), rgba(24, 24, 27, 0.95))';
        iconColor = 'text-yellow-400';
        icon = `<svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M12 9v4M12 17h.01M12 3l-9 15h18L12 3z"></path></svg>`;
        break;
      default:
        toast.classList.add('border-l-4', 'border-blue-500', 'text-white');
        bgGradient = 'linear-gradient(to right, rgba(30, 58, 138, 0.9), rgba(24, 24, 27, 0.95))';
        iconColor = 'text-blue-400';
        icon = `<svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"></circle><line x1="12" x2="12" y1="16" y2="12"></line><line x1="12" x2="12.01" y1="8" y2="8"></line></svg>`;
    }
    
    toast.style.background = bgGradient;
    toast.innerHTML = `
      <div class="flex items-center p-3 w-full">
        <div class="flex-shrink-0 ${iconColor} mr-3">${icon}</div>
        <div class="flex-1 font-medium text-sm pr-2">${message}</div>
        <button class="text-white/70 hover:text-white transition-colors ml-1 flex-shrink-0">
          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <line x1="18" y1="6" x2="6" y2="18"></line>
            <line x1="6" y1="6" x2="18" y2="18"></line>
          </svg>
        </button>
      </div>
    `;
    
    // Smooth entrance animation
    toast.style.opacity = '0';
    toast.style.transform = 'translateX(100%)';
    toastContainer.appendChild(toast);
    
    requestAnimationFrame(() => {
      toast.style.transition = 'all 0.3s cubic-bezier(0.68, -0.55, 0.265, 1.55)';
      toast.style.opacity = '1';
      toast.style.transform = 'translateX(0)';
    });
    
    // Close button handler
    const closeButton = toast.querySelector('button');
    const closeToast = () => {
      toast.style.opacity = '0';
      toast.style.transform = 'translateX(100%)';
      setTimeout(() => {
        if (toast.parentNode) {
          toast.remove();
        }
      }, 300);
    };
    
    closeButton.addEventListener('click', closeToast);
    
    // Auto-close after 4 seconds
    setTimeout(closeToast, 4000);
  }, []);

  const validateEmail = useCallback((email) => {
    return email.match(/^[^\s@]+@[^\s@]+\.[^\s@]+$/);
  }, []);

  const handleEmailSubmit = async (e) => {
    e.preventDefault();
    
    if (!email) {
      showToast('Please enter your email address.', 'error');
      return;
    }
    
    if (!validateEmail(email)) {
      showToast('Please enter a valid email address.', 'error');
      return;
    }
    
    setIsLoading(true);
    
    try {
      const success = await login(email);
      
      if (success) {
        setDisplayEmail(email);
        showToast('Verification code sent to your email.', 'success');
        // setCurrentStep artık useEffect'te ayarlanacak
        startResendTimer();
      } else {
        showToast('Failed to send verification code.', 'error');
      }
    } catch (error) {
      showToast(error.message || 'Connection error. Please check your internet.', 'error');
    } finally {
      setIsLoading(false);
    }
  };

  const handleOtpChange = (index, value) => {
    if (!/^\d*$/.test(value)) return;
    
    const newOtp = [...otp];
    newOtp[index] = value;
    setOtp(newOtp);
    
    // Auto-focus next input
    if (value && index < 5) {
      otpInputs.current[index + 1]?.focus();
    }
    
    // Auto-verify when all fields are filled
    if (newOtp.every(digit => digit) && newOtp.join('').length === 6) {
      handleVerifyOtp(newOtp.join(''));
    }
  };

  const handleOtpKeyDown = (index, e) => {
    if (e.key === 'Backspace' && !otp[index] && index > 0) {
      otpInputs.current[index - 1]?.focus();
    }
  };

  const handleOtpPaste = (e) => {
    e.preventDefault();
    const pasteData = e.clipboardData.getData('text').replace(/\D/g, '').substring(0, 6);
    const newOtp = pasteData.split('').concat(Array(6).fill('')).slice(0, 6);
    setOtp(newOtp);
    
    if (pasteData.length === 6) {
      handleVerifyOtp(pasteData);
    } else if (pasteData.length > 0 && pasteData.length < 6) {
      otpInputs.current[pasteData.length]?.focus();
    }
  };

  const handleVerifyOtp = async (otpValue = null) => {
  const otpString = otpValue || otp.join('');
  
  if (otpString.length !== 6) {
    return;
  }
  
  setIsLoading(true);
  
  try {

    const result = await verifyOTP(otpString);

    
    if (result === true) {
      // Mevcut kullanıcı - başarılı giriş
      showToast('Login successful! Redirecting...', 'success');
      setTimeout(() => {
        navigate('/', { replace: true });
      }, 1000);
    } else if (result && result.needsProfileSetup) {
      // Yeni kullanıcı - profil kurulum gerekli
      showToast('Verification successful! Please complete your profile.', 'success');
      setCurrentStep('profile');
    } else {
      showToast('Invalid verification code. Please try again.', 'error');
    }
  } catch (error) {

    // Provide more specific error messages
    if (error.message.includes('expired')) {
      showToast('Verification code has expired. Please request a new one.', 'error');
      // Reset to email step to get new OTP
      setCurrentStep('email');
      setOtp(['', '', '', '', '', '']);
    } else if (error.message.includes('invalid') || error.message.includes('Invalid')) {
      showToast('Invalid verification code. Please check and try again.', 'error');
      // Clear OTP inputs
      setOtp(['', '', '', '', '', '']);
      if (otpInputs.current[0]) {
        otpInputs.current[0].focus();
      }
    } else {
      showToast(error.message || 'Verification failed. Please try again.', 'error');
    }
  } finally {
    setIsLoading(false);
  }
};

  const handleProfileSubmit = async (e) => {
  e.preventDefault();
  
  if (!fullname.trim()) {
    showToast('Please enter your full name.', 'error');
    return;
  }
  
  if (!experience) {
    showToast('Please select your trading experience.', 'error');
    return;
  }
  
  setIsLoading(true);
  
  try {
 
    
    const success = await completeRegistration({ 
      fullname: fullname.trim(), 
      experience 
    });
    
  
    
    if (success) {
      showToast('Account created successfully!', 'success');
      setTimeout(() => {
        navigate('/', { replace: true });
      }, 1500);
    } else {
      showToast('Failed to create account. Please try again.', 'error');
    }
  } catch (error) {

    // Provide more specific error messages
    if (error.message.includes('Session expired') || error.message.includes('OTP not verified')) {
      showToast('Session expired. Please start the registration process again.', 'error');
      // Reset to email step
      setCurrentStep('email');
      setOtp(['', '', '', '', '', '']);
      setFullname('');
      setExperience('');
    } else if (error.message.includes('email')) {
      showToast('Email verification issue. Please start again.', 'error');
      setCurrentStep('email');
    } else {
      showToast(error.message || 'Connection error. Please check your internet.', 'error');
    }
  } finally {
    setIsLoading(false);
  }
};


  const startResendTimer = useCallback(() => {
    setResendTimer(60);
    resendIntervalRef.current = setInterval(() => {
      setResendTimer((prev) => {
        if (prev <= 1) {
          clearInterval(resendIntervalRef.current);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  }, []);

const handleBackToEmail = useCallback(() => {
  setCurrentStep('email');
  setOtp(['', '', '', '', '', '']);
  setFullname('');
  setExperience('');
  
  if (resendIntervalRef.current) {
    clearInterval(resendIntervalRef.current);
    setResendTimer(0);
  }
  
  // Reset any auth context state if needed
  // You might want to call a reset function from auth context here
}, []);

  // Add CSS styles to head (optimized)
  useEffect(() => {
    const styleId = 'login-page-styles';
    if (document.getElementById(styleId)) return;

    const style = document.createElement('style');
    style.id = styleId;
    style.textContent = `
      body {
        font-family: 'Inter', sans-serif;
        margin: 0;
        padding: 0;
        overflow-x: hidden;
        height: 100vh;
        width: 100vw;
        background-color: #000;
      }

      .shimmer-button {
        position: relative;
        overflow: hidden;
        background: #000000;
        color: white;
        border-radius: 0.5rem;
        transition: all 0.3s ease;
        transform: translateZ(0); /* Hardware acceleration */
      }

      .shimmer-button::before {
        content: '';
        position: absolute;
        top: 0;
        width: 100px;
        height: 100%;
        background: linear-gradient(
          90deg,
          transparent,
          rgba(34, 197, 94, 0.3),
          transparent
        );
        animation: shimmer 3s infinite linear;
        transform: skewX(-20deg) translateZ(0);
        will-change: transform;
      }

      @keyframes shimmer {
        0% { left: -100px; }
        100% { left: 100%; }
      }

      #spline-container {
        position: fixed;
        top: 0;
        left: 0;
        width: 100vw;
        height: 100vh;
        z-index: 0;
        overflow: hidden;
        will-change: transform;
      }

      #spline-canvas {
        position: absolute;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        display: block;
        will-change: transform;
      }

      .otp-container {
        display: flex;
        gap: 8px;
        justify-content: center;
      }

      .otp-input {
        width: 40px;
        height: 48px;
        border-radius: 8px;
        border: 2px solid #3f3f46;
        background-color: #18181b;
        color: white;
        font-size: 20px;
        font-weight: 600;
        text-align: center;
        transition: all 0.2s ease;
      }

      .otp-input:focus {
        border-color: #22c55e;
        outline: none;
        box-shadow: 0 0 0 2px rgba(34, 197, 94, 0.3);
      }

      .form-transition {
        transition: all 0.5s cubic-bezier(0.4, 0, 0.2, 1);
        transform: translateZ(0); /* Hardware acceleration */
      }

      .form-transition.exit {
        opacity: 0;
        transform: translateY(-20px) translateZ(0);
      }

      .form-transition.enter {
        opacity: 0;
        transform: translateY(20px) translateZ(0);
      }

      .form-transition.active {
        opacity: 1;
        transform: translateY(0) translateZ(0);
      }

      input:focus {
        outline: none;
        box-shadow: 0 0 0 2px rgba(34, 197, 94, 0.5);
      }

      .backdrop-blur-form {
        backdrop-filter: blur(12px);
        -webkit-backdrop-filter: blur(12px);
      }

      ::-webkit-scrollbar {
        display: none;
      }

      button:disabled {
        opacity: 0.7;
        cursor: not-allowed;
      }

      /* Performance optimizations */
      * {
        box-sizing: border-box;
      }

      .animate-spin {
        animation: spin 1s linear infinite;
      }

      @keyframes spin {
        to { transform: rotate(360deg); }
      }
    `;
    document.head.appendChild(style);

    // Load external resources
    const resources = [
      {
        type: 'script',
        src: 'https://cdn.tailwindcss.com',
        id: 'tailwind-css'
      },
      {
        type: 'link',
        href: 'https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&display=swap',
        id: 'inter-font',
        rel: 'stylesheet'
      }
    ];

    resources.forEach(resource => {
      if (!document.getElementById(resource.id)) {
        const element = document.createElement(resource.type);
        element.id = resource.id;
        if (resource.type === 'script') {
          element.src = resource.src;
        } else {
          element.href = resource.href;
          element.rel = resource.rel;
        }
        document.head.appendChild(element);
      }
    });

    return () => {
      const styleElement = document.getElementById(styleId);
      if (styleElement) {
        styleElement.remove();
      }
    };
  }, []);

  return (
    <div className="dark">
      {/* Spline Background Container */}
      <div id="spline-container">
        {/* Canvas will be added here via JavaScript */}
      </div>
      
      {/* Flex container for the layout */}
      <div className="relative z-10 flex min-h-screen w-full pointer-events-none">
        {/* Left Panel - Text Overlay */}
        <div className="hidden w-1/2 md:block pointer-events-none">
          <div className="flex h-full flex-col justify-between">
            <div className="relative h-full w-full">
              {/* Brand Name */}
              <div className="absolute left-8 top-8">
                <h1 className="text-2xl font-bold text-white tracking-tight">Wells.</h1>
              </div>
              
              {/* Main Heading */}
              <div className="absolute inset-0 flex flex-col items-center justify-center text-center px-8">
                <div className="space-y-6">
                  <div className="inline-block px-3 py-1 bg-green-500/10 backdrop-blur-sm rounded-full">
                    <span className="text-xs font-medium text-green-400">AI-Powered Trading System</span>
                  </div>
                  
                  <h1 className="text-5xl sm:text-6xl font-bold text-white leading-tight tracking-tighter">
                   When the market talks,<br/>AI listens.
                  </h1>
                  
                  <p className="text-lg text-gray-300 max-w-2xl leading-relaxed">
                    Imagine a system that trades smarter, faster, and more accurately than you ever could. Welcome to the future of trading.
                  </p>
                  
                  <p className="text-xl font-medium text-white mt-2">
                    Are you ready to trade like never before?
                  </p>
                  
                  <div className="flex flex-wrap items-center justify-center gap-4 mt-6">
                    <div className="flex items-center space-x-1">
                      <div className="w-2 h-2 rounded-full bg-green-500"></div>
                      <span className="text-sm text-gray-400">Advanced AI</span>
                    </div>
                    <div className="flex items-center space-x-1">
                      <div className="w-2 h-2 rounded-full bg-green-500"></div>
                      <span className="text-sm text-gray-400">Real-time Analysis</span>
                    </div>
                    <div className="flex items-center space-x-1">
                      <div className="w-2 h-2 rounded-full bg-green-500"></div>
                      <span className="text-sm text-gray-400">Secure Platform</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        
        {/* Right Panel - Auth Flow */}
        <div className="flex w-full items-center justify-center md:w-1/2">
          {/* Auth container */}
          <div className="w-full max-w-md px-4 sm:px-8 pointer-events-auto form-container">
            {/* Step 1: Email Entry */}
            {currentStep === 'email' && (
              <div className="rounded-xl bg-zinc-800/80 backdrop-blur-form p-6 sm:p-8 border border-zinc-700/50 shadow-[0_8px_30px_rgb(0,0,0,0.12)] form-transition active">
                <div className="mb-6 text-center">
                  <h2 className="text-2xl font-bold tracking-tight text-white">Welcome to Wells</h2>
                  <p className="mt-2 text-sm text-zinc-400">
                    Enter your email to sign in or create an account
                  </p>
                </div>
                
                <form onSubmit={handleEmailSubmit} className="space-y-5">
                  <div className="space-y-2">
                    <label htmlFor="email" className="text-white text-sm font-medium">
                      Email Address
                    </label>
                    <div className="relative">
                      <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
                        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="h-4 w-4 text-zinc-500">
                          <rect width="20" height="16" x="2" y="4" rx="2"></rect>
                          <path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7"></path>
                        </svg>
                      </div>
                      <input 
                        id="email" 
                        type="email" 
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        placeholder="definitely.not.spam@trustme.com"
                        className="bg-zinc-900 border border-zinc-700 text-white placeholder:text-zinc-500 pl-10 rounded-lg w-full py-2 px-3 focus:outline-none focus:ring-2 focus:ring-green-500"
                        required
                        disabled={isLoading}
                      />
                    </div>
                  </div>
                  
                  <button 
                    type="submit"
                    className="w-full py-3 text-sm font-medium shimmer-button"
                    disabled={isLoading}
                  >
                    <span className="flex items-center justify-center">
                      {isLoading ? (
                        <>
                          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="h-4 w-4 mr-2 animate-spin">
                            <path d="M21 12a9 9 0 1 1-6.219-8.56"></path>
                          </svg>
                          Sending code...
                        </>
                      ) : (
                        <>
                          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="h-4 w-4 mr-2">
                            <path d="M22 7 L13.5 12 2 7"></path>
                            <path d="M2 7V17C2 17.5304 2.21071 18.0391 2.58579 18.4142C2.96086 18.7893 3.46957 19 4 19H20C20.5304 19 21.0391 18.7893 21.4142 18.4142C21.7893 18.0391 22 17.5304 22 17V7"></path>
                            <path d="M2 7L13.5 12 22 7"></path>
                          </svg>
                          Continue with Email
                        </>
                      )}
                    </span>
                  </button>
                </form>
                
                <div className="mt-6 text-center text-xs text-zinc-500">
                  <p>
                    By continuing, you agree to our
                    <a href="#" className="text-green-500 hover:text-green-400 transition-colors"> Terms of Service</a> and
                    <a href="#" className="text-green-500 hover:text-green-400 transition-colors"> Privacy Policy</a>.
                  </p>
                </div>
              </div>
            )}
            
            {/* Step 2: OTP Verification */}
            {currentStep === 'otp' && (
              <div className="rounded-xl bg-zinc-800/80 backdrop-blur-form p-6 sm:p-8 border border-zinc-700/50 shadow-[0_8px_30px_rgb(0,0,0,0.12)] form-transition active">
                <div className="mb-6 text-center">
                  <h2 className="text-2xl font-bold tracking-tight text-white">Check your email</h2>
                  <p className="mt-2 text-sm text-zinc-400">
                    We've sent a verification code to <span className="text-white font-medium">{displayEmail}</span>
                  </p>
                  {/* Yeni kullanıcı bildirimi eklendi */}
                  {isNewUser && (
                    <div className="mt-3 p-2 bg-green-500/10 rounded-lg border border-green-500/20">
                      <p className="text-xs text-green-400">
                        🎉 New account detected! After verification, you'll set up your profile.
                      </p>
                    </div>
                  )}
                </div>
                
                <div className="space-y-5">
                  <div className="space-y-4">
                    <label className="text-white text-sm font-medium block text-center">
                      Enter the 6-digit code
                    </label>
                    
                    <div className="otp-container">
                        {otp.map((digit, index) => (
                        <input
                          key={index}
                          ref={(el) => (otpInputs.current[index] = el)}
                          type="text"
                          inputMode="numeric"
                          pattern="[0-9]*"
                          maxLength="1"
                          value={digit}
                          onChange={(e) => handleOtpChange(index, e.target.value)}
                          onKeyDown={(e) => handleOtpKeyDown(index, e)}
                          onPaste={handleOtpPaste}
                          className="otp-input"
                          disabled={isLoading}
                          autoComplete="one-time-code"
                        />
                      ))}
                    </div>
              
                    
                    <p className="text-center text-sm text-zinc-400">
                      Didn't receive the code? 
                      <button 
                        type="button" 
                        className="text-green-500 hover:text-green-400 font-medium disabled:opacity-50 disabled:pointer-events-none ml-1" 
                        disabled={resendTimer > 0}
                        onClick={() => handleEmailSubmit({ preventDefault: () => {} })}
                      >
                        {resendTimer > 0 ? `Resend in ${resendTimer}s` : 'Resend code'}
                      </button>
                    </p>
                  </div>
                  
                  <button 
                    type="button"
                    onClick={() => handleVerifyOtp()}
                    className="w-full py-3 text-sm font-medium shimmer-button"
                    disabled={isLoading || !otp.every(digit => digit)}
                  >
                    <span className="flex items-center justify-center">
                      {isLoading ? (
                        <>
                          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="h-4 w-4 mr-2 animate-spin">
                            <path d="M21 12a9 9 0 1 1-6.219-8.56"></path>
                          </svg>
                          Verifying...
                        </>
                      ) : (
                        <>
                          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="h-4 w-4 mr-2">
                            <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
                            <polyline points="22 4 12 14.01 9 11.01"></polyline>
                          </svg>
                          {isNewUser ? 'Verify & Continue' : 'Verify Code'}
                        </>
                      )}
                    </span>
                  </button>
                  
                  <button 
                    type="button"
                    className="w-full py-2 text-sm font-medium text-zinc-400 hover:text-white transition-colors"
                    onClick={handleBackToEmail}
                    disabled={isLoading}
                  >
                    <span className="flex items-center justify-center">
                      <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="h-4 w-4 mr-2">
                        <path d="m15 18-6-6 6-6"></path>
                      </svg>
                      Change Email
                    </span>
                  </button>
                </div>
              </div>
            )}
            
            {/* Step 3: New User Profile Setup */}
            {currentStep === 'profile' && (
              <div className="rounded-xl bg-zinc-800/80 backdrop-blur-form p-6 sm:p-8 border border-zinc-700/50 shadow-[0_8px_30px_rgb(0,0,0,0.12)] form-transition active">
                <div className="mb-6 text-center">
                  <h2 className="text-2xl font-bold tracking-tight text-white">Complete Your Profile</h2>
                  <p className="mt-2 text-sm text-zinc-400">
                    Just a few more details to get you started
                  </p>
                </div>
                
                <form onSubmit={handleProfileSubmit} className="space-y-5">
                  <div className="space-y-2">
                    <label htmlFor="fullname" className="text-white text-sm font-medium">
                      Full Name
                    </label>
                    <div className="relative">
                      <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
                        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="h-4 w-4 text-zinc-500">
                          <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path>
                          <circle cx="12" cy="7" r="4"></circle>
                        </svg>
                      </div>
                      <input 
                        id="fullname" 
                        type="text" 
                        value={fullname}
                        onChange={(e) => setFullname(e.target.value)}
                        placeholder="Enter your full name"
                        className="bg-zinc-900 border border-zinc-700 text-white placeholder:text-zinc-500 pl-10 rounded-lg w-full py-2 px-3 focus:outline-none focus:ring-2 focus:ring-green-500"
                        required
                        disabled={isLoading}
                      />
                    </div>
                  </div>
                  
                  <div className="space-y-2">
                    <label className="text-white text-sm font-medium">
                      Trading Experience
                    </label>
                    <div className="grid grid-cols-1 gap-2">
                      {[
                        { value: 'beginner', label: 'Beginner', description: 'New to trading' },
                        { value: 'intermediate', label: 'Intermediate', description: 'Some trading experience' },
                        { value: 'advanced', label: 'Advanced', description: 'Experienced trader' }
                      ].map((option) => (
                        <button
                          key={option.value}
                          type="button"
                          onClick={() => setExperience(option.value)}
                          className={`text-left p-4 rounded-lg border-2 transition-all ${
                            experience === option.value
                              ? 'border-green-500 text-white bg-zinc-800'
                              : 'bg-zinc-900 border-zinc-700 text-zinc-400 hover:border-zinc-600'
                          }`}
                          disabled={isLoading}
                        >
                          <div className="font-medium">{option.label}</div>
                          <div className="text-sm text-zinc-500">{option.description}</div>
                        </button>
                      ))}
                    </div>
                  </div>
                  
                  <button 
                    type="submit"
                    className="w-full py-3 text-sm font-medium shimmer-button"
                    disabled={isLoading || !fullname.trim() || !experience}
                  >
                    <span className="flex items-center justify-center">
                      {isLoading ? (
                        <>
                          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="h-4 w-4 mr-2 animate-spin">
                            <path d="M21 12a9 9 0 1 1-6.219-8.56"></path>
                          </svg>
                          Creating account...
                        </>
                      ) : (
                        <>
                          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="h-4 w-4 mr-2">
                            <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
                            <polyline points="22 4 12 14.01 9 11.01"></polyline>
                          </svg>
                          Complete Registration
                        </>
                      )}
                    </span>
                  </button>
                </form>
              </div>
            )}
            
            {/* Footer */}
            <div className="mt-6 text-center pointer-events-auto">
              <p className="mt-2 text-xs text-zinc-600">
                &copy; {new Date().getFullYear()} Wells Finance. All rights reserved.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Toast Notification Container */}
      <div id="toast-container" className="fixed top-4 right-4 z-50 flex flex-col gap-2"></div>
    </div>
  );
};

export default LoginPage;